import { useState, useMemo, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Select from "react-select";
import countryList from "react-select-country-list";
import { allCountries } from "country-telephone-data";
import { GoogleLogin } from "@react-oauth/google";
import { toast } from "react-toastify";
import {
  FiHome,
  FiUser,
  FiMail,
  FiPhone,
  FiLock,
  FiEye,
  FiEyeOff,
  FiUserPlus
} from "react-icons/fi";

import API_BASE_URL from "../config";
import "../styles/Signup.css";

export default function Signup() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [logoUrl, setLogoUrl] = useState(null);
  const [emailStatus, setEmailStatus] = useState("idle");

  /* ==============================
     LOAD LOGO FROM SITE SETTINGS
  ============================== */
  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/site-settings`);
        const data = await res.json();

        if (data?.logo && data?.logoContentType) {
          const base64 = `data:${data.logoContentType};base64,${data.logo}`;
          setLogoUrl(base64);
        }
      } catch (err) {
        console.error("Failed to load logo:", err);
      }
    };

    fetchLogo();
  }, []);

  /* ======================
     COUNTRY CODE OPTIONS
  ====================== */
  const countryOptions = useMemo(() => {
    return countryList().getData().map((c) => {
      const tel = allCountries.find(
        (t) => t.iso2 === c.value.toLowerCase()
      );
      return {
        label: `${c.label} ${tel ? "(+" + tel.dialCode + ")" : ""}`,
        value: tel ? "+" + tel.dialCode : ""
      };
    });
  }, []);

  /* ======================
     FORM STATE
  ====================== */
  const [form, setForm] = useState({
    name: "",
    email: "",
    countryCode: "+44",
    mobile: "",
    password: "",
    confirmPassword: ""
  });

  /* ==============================
     PASSWORD STRENGTH CALCULATION
  ============================== */
  const calculatePasswordStrength = (password) => {
    let strength = 0;
    if (password.length >= 8) strength += 1;
    if (/[A-Z]/.test(password)) strength += 1;
    if (/[0-9]/.test(password)) strength += 1;
    if (/[^A-Za-z0-9]/.test(password)) strength += 1;
    return strength;
  };

  const passwordStrength = useMemo(() =>
    calculatePasswordStrength(form.password),
    [form.password]
  );

  // eslint-disable-next-line no-unused-vars
  const getStrengthClass = () => {
    if (form.password.length === 0) return "";
    if (passwordStrength <= 1) return "strength-weak";
    if (passwordStrength <= 2) return "strength-medium";
    return "strength-strong";
  };

  /* ==============================
     VALIDATION FUNCTIONS
  ============================== */
  const validateStep1 = () => {
    if (!form.name.trim()) {
      toast.error("Please enter your full name");
      return false;
    }

    if (!form.email.trim()) {
      toast.error("Please enter your email address");
      return false;
    }

    if (emailStatus === "exists") {
      toast.error("This email is already registered. Please login.");
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      toast.error("Please enter a valid email address");
      return false;
    }

    if (!form.mobile.trim()) {
      toast.error("Please enter your mobile number");
      return false;
    }

    return true;
  };


  const validateStep2 = () => {
    const password = form.password;

    if (password.length < 8) {
      toast.error("Password must be at least 8 characters long");
      return false;
    }

    if (!/[A-Z]/.test(password)) {
      toast.error("Password must contain at least one uppercase letter");
      return false;
    }

    if (!/[0-9]/.test(password)) {
      toast.error("Password must contain at least one number");
      return false;
    }

    if (!/[^A-Za-z0-9]/.test(password)) {
      toast.error("Password must contain at least one special character");
      return false;
    }

    if (password !== form.confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }

    return true;
  };


  /* ==============================
     HANDLE KEY PRESS
  ============================== */
  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (step === 1) {
        handleNextStep();
      } else {
        sendOtp();
      }
    }
  };

  /* ======================
     STEP NAVIGATION
  ====================== */
  const handleNextStep = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handlePrevStep = () => {
    setStep(1);
  };

  /* ======================
     🔵 GOOGLE SIGNUP
  ====================== */
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          token: credentialResponse.credential
        })
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Google signup failed");
        setLoading(false);
        return;
      }

      toast.success("Google verified. OTP sent to your email");

      // Redirect to OTP verification
      navigate("/verify", {
        state: {
          form: {
            name: data.name,
            email: data.email,
            countryCode: "",
            mobile: "",
            password: ""
          }
        }
      });

    } catch (err) {
      console.error(err);
      toast.error("Google signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  /* ======================
     🔵 NORMAL SIGNUP → SEND OTP
  ====================== */
  const sendOtp = async () => {
    if (!validateStep2()) return;

    try {
      setLoading(true);

      const res = await fetch(`${API_BASE_URL}/api/auth/send-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: form.email,
          mobile: form.countryCode + form.mobile,
          name: form.name
        })
      });

      const data = await res.json();

      if (!res.ok) {
        toast.error(data.message || "Failed to send OTP");
        setLoading(false);
        return;
      }

      toast.success("OTP sent successfully to your email");

      // Redirect to verification page
      navigate("/verify", {
        state: {
          form: {
            name: form.name,
            email: form.email,
            countryCode: form.countryCode,
            mobile: form.mobile,
            password: form.password
          }
        }
      });

    } catch (err) {
      console.error(err);
      toast.error("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!form.email) {
      setEmailStatus("idle");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      setEmailStatus("idle");
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setEmailStatus("checking");

        const res = await fetch(`${API_BASE_URL}/api/auth/check-email`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: form.email })
        });

        const data = await res.json();

        if (data.available) {
          setEmailStatus("available");
        } else {
          setEmailStatus("exists");
        }

      } catch (err) {
        setEmailStatus("idle");
      }
    }, 700); // debounce delay

    return () => clearTimeout(timer);

  }, [form.email]);

  /* ==============================
   PASSWORD MATCH CHECK
============================== */
  const passwordsMatch =
    form.password.length > 0 &&
    form.confirmPassword.length > 0 &&
    form.password === form.confirmPassword;



  /* ======================
     SELECT STYLES
  ====================== */
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      minHeight: "52px",
      border: "2px solid #e5e7eb",
      borderRadius: "10px",
      boxShadow: "none",
      "&:hover": {
        borderColor: "#d1d5db"
      },
      "&:focus-within": {
        borderColor: "#c8ab68",
        boxShadow: "0 0 0 3px rgba(200, 171, 104, 0.2)"
      }
    }),
    placeholder: (base) => ({
      ...base,
      color: "#9ca3af"
    }),
    singleValue: (base) => ({
      ...base,
      color: "#1f2937"
    }),
    input: (base) => ({
      ...base,
      color: "#1f2937"
    }),
    menu: (base) => ({
      ...base,
      borderRadius: "10px",
      border: "2px solid #e5e7eb",
      boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)"
    })
  };

  return (
    <div className="premium-signup-page">

      {/* ==============================
         TOP RIGHT HOME BUTTON ONLY
      ============================== */}
      <div className="premium-signup-home-container">
        <Link to="/" className="premium-signup-home-btn">
          <FiHome size={20} />
          <span>Home</span>
        </Link>
      </div>

      {/* ==============================
         MAIN CONTENT
      ============================== */}
      <div className="premium-signup-container">
        {/* Signup Form */}
        <div className="premium-signup-form-container">
          <div className="premium-signup-form-card">
            {/* Logo */}
            <div className="premium-signup-form-logo">
              {logoUrl ? (
                <img src={logoUrl} alt="Brand Logo" className="premium-signup-form-brand-logo" />
              ) : (
                <div className="premium-signup-form-brand-text">Justivon</div>
              )}
            </div>

            <div className="premium-signup-form-header">
              <h2 className="premium-signup-form-title">Create Account</h2>
              <p className="premium-signup-form-subtitle">
                Join our legal platform and get started today
              </p>
            </div>

            {/* Google Signup - Only on Step 1 */}
            {step === 1 && (
              <>
                <div className="premium-signup-google-wrapper">
                  <GoogleLogin
                    onSuccess={handleGoogleSuccess}
                    onError={() => toast.error("Google signup failed")}
                    theme="outline"
                    size="large"
                    width="100%"
                    shape="rectangular"
                    text="continue_with"
                    locale="en"
                    useOneTap={false}
                  />
                </div>


                <div className="premium-signup-divider">
                  <span>Or sign up with email</span>
                </div>
              </>
            )}

            {/* STEP 1: Personal Information */}
            {step === 1 && (
              <>
                {/* Full Name Field */}
                <div className="premium-signup-form-group">
                  <label className="premium-signup-label">
                    <FiUser size={16} />
                    <span>Full Name</span>
                  </label>
                  <div className="premium-signup-input-group">
                    <input
                      className="premium-signup-input"
                      type="text"
                      placeholder="Enter your full name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      onKeyPress={handleKeyPress}
                    />
                  </div>
                </div>

                {/* Email Field */}
                {/* Email Field */}
                <div className="premium-signup-form-group">
                  <label className="premium-signup-label">
                    <FiMail size={16} />
                    <span>Email Address</span>
                  </label>

                  <div className="premium-signup-input-group">
                    <input
                      className="premium-signup-input"
                      type="email"
                      placeholder="Enter your email address"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      onKeyPress={handleKeyPress}
                    />
                  </div>

                  {/* EMAIL STATUS */}
                  {emailStatus === "checking" && (
                    <p className="email-status checking">Checking email...</p>
                  )}

                  {emailStatus === "available" && (
                    <p className="email-status success">Email available</p>
                  )}

                  {emailStatus === "exists" && (
                    <p className="email-status error">Email already exists</p>
                  )}
                </div>


                {/* Phone Field */}
                <div className="premium-signup-form-group">
                  <label className="premium-signup-label">
                    <FiPhone size={16} />
                    <span>Phone Number</span>
                  </label>
                  <div className="phone-input-group">
                    <div className="country-select-wrapper">
                      <Select
                        options={countryOptions}
                        value={countryOptions.find(
                          (c) => c.value === form.countryCode
                        )}
                        onChange={(s) =>
                          setForm({ ...form, countryCode: s.value })
                        }
                        styles={selectStyles}
                        placeholder="Code"
                        className="country-select"
                      />
                    </div>
                    <div className="phone-input-wrapper">
                      <input
                        className="premium-signup-input"
                        type="tel"
                        placeholder="Mobile Number"
                        value={form.mobile}
                        onChange={(e) =>
                          setForm({ ...form, mobile: e.target.value })
                        }
                        onKeyPress={handleKeyPress}
                      />
                    </div>
                  </div>
                </div>

                {/* Next Button */}
                <button
                  className="premium-signup-submit-btn"
                  onClick={handleNextStep}
                  disabled={loading || emailStatus === "exists" || emailStatus === "checking"}
                >
                  <span>
                    {emailStatus === "exists"
                      ? "Email Already Registered"
                      : "Continue to Password"}
                  </span>
                </button>

              </>
            )}

            {/* STEP 2: Password Setup */}
            {step === 2 && (
              <>

                {/* Password Field */}
                <div className="premium-signup-form-group">
                  <label className="premium-signup-label">
                    <FiLock size={16} />
                    <span>Create Password</span>
                  </label>
                  <div className="premium-signup-input-group premium-signup-password-group">
                    <input
                      className="premium-signup-input"
                      type={showPassword ? "text" : "password"}
                      placeholder="Create a strong password"
                      value={form.password}
                      onChange={(e) => setForm({ ...form, password: e.target.value })}
                      onKeyPress={handleKeyPress}
                    />
                    <button
                      className="premium-signup-password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                      type="button"
                      aria-label={showPassword ? "Hide password" : "Show password"}
                    >
                      {showPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>

                  <div className="password-hint">
                    Must contain at least 8 characters, one uppercase letter, one number and one special character
                  </div>

                </div>

                {/* Confirm Password Field */}
                {/* Confirm Password Field */}
                <div className="premium-signup-form-group">
                  <label className="premium-signup-label">
                    <FiLock size={16} />
                    <span>Confirm Password</span>
                  </label>

                  <div className="premium-signup-input-group premium-signup-password-group">
                    <input
                      className="premium-signup-input"
                      type={showConfirmPassword ? "text" : "password"}
                      placeholder="Confirm your password"
                      value={form.confirmPassword}
                      onChange={(e) =>
                        setForm({ ...form, confirmPassword: e.target.value })
                      }
                      onKeyPress={handleKeyPress}
                    />

                    <button
                      className="premium-signup-password-toggle"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      type="button"
                      aria-label={showConfirmPassword ? "Hide password" : "Show password"}
                    >
                      {showConfirmPassword ? <FiEyeOff size={18} /> : <FiEye size={18} />}
                    </button>
                  </div>

                  {/* PASSWORD MATCH STATUS */}
                  {form.confirmPassword && (
                    <p
                      className={`password-match-status ${passwordsMatch ? "success" : "error"
                        }`}
                    >
                      {passwordsMatch ? "✔ Passwords match" : "✖ Passwords do not match"}
                    </p>
                  )}
                </div>


                {/* Complete Signup Button */}
                <button
                  className="premium-signup-submit-btn"
                  onClick={sendOtp}
                  disabled={
                    loading ||
                    !passwordsMatch ||          // 🔒 disable if not match
                    !form.password ||
                    !form.confirmPassword
                  }
                >
                  {loading ? (
                    <div className="premium-signup-spinner"></div>
                  ) : (
                    <FiUserPlus size={18} />
                  )}
                  <span>
                    {loading
                      ? "Sending OTP..."
                      : !passwordsMatch
                        ? "Passwords Not Matching"
                        : "Complete Signup"}
                  </span>
                </button>

              </>
            )}

            {/* Sign In Link */}
            <div className="premium-signup-signin-link">
              <span>Already have an account?</span>
              <Link to="/login" className="premium-signup-signin-btn">
                <FiUser size={16} />
                <span>Sign In</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}